import { Alert, Collapse, Spin, Table, Checkbox, message, Tag, Tooltip, Modal, Input, Empty, Typography, Pagination } from 'antd';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { userApi } from '../../services/actions/users';
import { useSelector } from 'react-redux';
import { PAGE_SIZE_OPTIONS } from '../../utils/table-pagination';

const { Panel } = Collapse;
const { Text } = Typography;

const PermissionCheckbox = React.memo(({ checked, onChange }) => (
    <Checkbox checked={checked} onChange={onChange} />
));

export const PermissionConfig = ({ id, type, assignedPermissions = [] }) => {
    const { company } = useSelector((state) => state.user);
    const [dataAssignedPermissions, setDataAssignedPermissions] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [contexts, setContexts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modifiedPermissions, setModifiedPermissions] = useState({});
    const [search, setSearch] = useState('');
    const [openEntities, setOpenEntities] = useState([]);
    const [contextPage, setContextPage] = useState({});
    const [contextPageSize, setContextPageSize] = useState({});

    const fetchPermissions = async () => {
        try {
            const response = await userApi.getPermission();
            const initialValues = response?.data || [];

            const groupedPermissions = initialValues.reduce((acc, item) => {
                if (!acc[item.entity]) {
                    acc[item.entity] = {
                        entity: item.entity,
                        name: item.entityName,
                        items: [],
                    };
                }
                acc[item.entity].items.push(item);
                return acc;
            }, {});

            setPermissions(Object.values(groupedPermissions));
        } catch {
            setError('Erro ao carregar as permissões');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPermissions();
    }, [id, type]);

    useEffect(() => {
        const fetchContexts = async () => {
            try {
                const response = await userApi.getContexts(company.unitId);
                setContexts(response?.data || []);
            } catch (ex) {
                setError(`Erro ao carregar os contextos: ${ex.message}`);
            }
        };

        fetchContexts();
    }, [company.unitId]);

    useEffect(() => {
        setDataAssignedPermissions(assignedPermissions);
    }, [assignedPermissions]);

    const contextsByEntity = useMemo(() => {
        return contexts.reduce((acc, context) => {
            if (!acc[context.entity_type]) acc[context.entity_type] = [];
            acc[context.entity_type].push(context);
            return acc;
        }, {});
    }, [contexts]);

    const term = search.trim().toLowerCase();
    const matches = (text) => (text || '').toLowerCase().includes(term);

    // Ao mudar a busca, volta todas as seções para a primeira página
    useEffect(() => {
        setContextPage({});
    }, [term]);

    const getPermissionOrigins = useCallback(
        (data) => {
            const matched = data.isContextual
                ? dataAssignedPermissions.find(
                    (item) => item.type === data.slug && data.context?.id === item.context?.id
                )
                : dataAssignedPermissions.find((item) => item.type === data.slug);
            return matched?.origins || [];
        },
        [dataAssignedPermissions]
    );

    const handlePermissionsChange = async (checked, permissionsToProcess, context) => {
        try {
            const typeEvent = checked ? 'add':'remove';
            if (permissionsToProcess.length <= 0) return;

            let actualPermissionsToProcess = [...permissionsToProcess];

            if (!checked && type === 'user') {
                const inheritedList = [];
                const directList = [];

                for (const item of permissionsToProcess) {
                    const origins = getPermissionOrigins({ ...item, context });
                    if (origins.some(o => o.type !== 'direct')) {
                        inheritedList.push(item);
                    } else {
                        directList.push(item);
                    }
                }

                if (inheritedList.length > 0) {
                    const inheritedNames = inheritedList.map(item => item.title).join(', ');
                    Modal.warning({
                        title: 'Permissões Herdadas Detectadas',
                        content: (
                            <div>
                                <p>As seguintes permissões são herdadas de grupos/papéis e não podem ser removidas individualmente: <b>{inheritedNames}</b>.</p>
                                {directList.length > 0 ? (
                                    <p>As outras permissões diretas serão removidas normalmente.</p>
                                ) : (
                                    <p>Para retirá-las, você deve alterar os grupos ou papéis correspondentes do usuário.</p>
                                )}
                            </div>
                        ),
                        okText: 'Entendido'
                    });

                    if (directList.length === 0) {
                        return; // Abort entirely if all of them are inherited
                    }
                    actualPermissionsToProcess = directList;
                }
            }

            const permissions = actualPermissionsToProcess.map(item => item.slug);

            let method;
            if (type === 'group') {
                method = 'editPermissionsFromGroup';
            } else if (type === 'role') {
                method = 'editPermissionsFromRole';
            } else {
                method = 'editPermissionsFromUser';
            }

            const params = type !== 'user'
                ? [permissions, id, company.unitId, context?.id || null, typeEvent]
                : [permissions, null, id, company.unitId, context?.id || null, typeEvent];

            await userApi[method](...params);
            setModifiedPermissions(prev => {
                const updated = { ...prev };
                for (const record of actualPermissionsToProcess) {
                    const key = context ? `${record.slug}-${context.id}` : record.slug;
                    updated[key] = checked;
                }
                return updated;
            });

        } catch (ex) {
            message.error(`Erro ao atualizar permissão! (${ex.message})`);
        }
    };

    const handlePermissionChange = async (record, checked) => {
        try {
            if (!checked && type === 'user') {
                const origins = getPermissionOrigins(record);
                const inheritedOrigins = origins.filter(o => o.type !== 'direct');
                if (inheritedOrigins.length > 0) {
                    const groupNames = inheritedOrigins.map(o => {
                        if (o.type === 'group') return `Grupo "${o.name}"`;
                        if (o.type === 'group_role') return `Grupo "${o.groupName}"`;
                        if (o.type === 'role') return `Papel "${o.name}"`;
                        return o.name || o.roleName;
                    }).join(', ');

                    Modal.warning({
                        title: 'Permissão Herdada',
                        content: (
                            <div>
                                <p>Esta permissão não pode ser removida individualmente porque o usuário a herda de: <b>{groupNames}</b>.</p>
                                <p>Para retirar essa permissão, você deve remover o usuário do grupo/papel correspondente ou alterar as permissões do próprio grupo/papel.</p>
                            </div>
                        ),
                        okText: 'Entendido'
                    });
                    return; // Prevent the API call!
                }
            }

            const method =
                checked && type === 'group'
                    ? 'addPermissionToGroup'
                    : !checked && type === 'group'
                        ? 'removePermissionFromGroup'
                        : checked && type === 'role'
                            ? 'addPermissionToRole'
                            : !checked && type === 'role'
                                ? 'removePermissionFromRole'
                                : checked && type === 'user'
                                    ? 'addPermissionToUser'
                                    : 'removePermissionFromUser';

            const params =
                type !== 'user'
                    ? [record.slug, id, company.unitId, record.context ? record.context.id : null]
                    : [record.slug, null, id, company.unitId, record.context ? record.context.id : null];

            await userApi[method](...params);

            const key = record.context ? `${record.slug}-${record.context.id}` : record.slug;
            setModifiedPermissions((prev) => ({
                ...prev,
                [key]: checked,
            }));
        } catch (ex) {
            message.error(`Erro ao atualizar permissão! (${ex.message})`);
        }
    };

    const isPermissionChecked = useCallback(
        (data) => {
            const key = data.context ? `${data.slug}-${data.context.id}` : data.slug;
            if (key in modifiedPermissions) return modifiedPermissions[key];

            return data.isContextual
                ? !!dataAssignedPermissions.find(
                    (item) => item.type === data.slug && data.context?.id === item.context?.id
                )
                : !!dataAssignedPermissions.find((item) => item.type === data.slug);
        },
        [dataAssignedPermissions, modifiedPermissions]
    );

    const getColumnsContext = (contextualItems, context) => {
        const allChecked = contextualItems.every((item) =>
            isPermissionChecked({ ...item, context })
        );


        const permissionsToProcess = contextualItems.filter((item) =>
            allChecked
                ? isPermissionChecked({ ...item, context }) // Desmarcar se todos estão marcados
                : !isPermissionChecked({ ...item, context }) // Marcar se nem todos estão marcados
        );

        return [
            {
                title: 'Nome',
                dataIndex: 'title',
                key: 'title',
            },
            {
                title: 'Descrição',
                dataIndex: 'description',
                key: 'description',
            },
            {
                title: (
                    <>
                        Permissão{' '}
                        <Checkbox
                            checked={allChecked}
                            onChange={(e) => handlePermissionsChange(e.target.checked,permissionsToProcess,context)}
                        />
                    </>
                ),
                dataIndex: 'permission',
                key: 'permission',
                align: 'center',
                width: 160,
                render: (_, record) => {
                    const checked = isPermissionChecked(record);
                    const origins = getPermissionOrigins(record);
                    const isInherited = type === 'user' && origins.some(o => o.type !== 'direct');

                    return (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                            <PermissionCheckbox
                                checked={checked}
                                onChange={(e) => handlePermissionChange(record, e.target.checked)}
                            />
                            {isInherited && (
                                <Tooltip title={
                                    <div>
                                        <b>Permissão herdada de:</b>
                                        {origins.filter(o => o.type !== 'direct').map((o, idx) => (
                                            <div key={idx} style={{ marginTop: '4px' }}>
                                                {o.type === 'group' ? `• Grupo: ${o.name}` : o.type === 'role' ? `• Papel: ${o.name}` : `• Papel do Grupo: ${o.roleName} (${o.groupName})`}
                                            </div>
                                        ))}
                                    </div>
                                }>
                                    <Tag color="blue" style={{ cursor: 'pointer' }}>Herdada</Tag>
                                </Tooltip>
                            )}
                        </div>
                    );
                },
            },
        ];
    };


    const columnsNoContext = [
        {
            title: 'Nome',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Descrição',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Permissão',
            dataIndex: 'permission',
            key: 'permission',
            align: 'center',
            width: 160,
            render: (_, record) => {
                const checked = isPermissionChecked(record);
                const origins = getPermissionOrigins(record);
                const isInherited = type === 'user' && origins.some(o => o.type !== 'direct');

                return (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <PermissionCheckbox
                            checked={checked}
                            onChange={(e) => handlePermissionChange(record, e.target.checked)}
                        />
                        {isInherited && (
                            <Tooltip title={
                                <div>
                                    <b>Permissão herdada de:</b>
                                    {origins.filter(o => o.type !== 'direct').map((o, idx) => (
                                        <div key={idx} style={{ marginTop: '4px' }}>
                                            {o.type === 'group' ? `• Grupo: ${o.name}` : o.type === 'role' ? `• Papel: ${o.name}` : `• Papel do Grupo: ${o.roleName} (${o.groupName})`}
                                        </div>
                                    ))}
                                </div>
                            }>
                                <Tag color="blue" style={{ cursor: 'pointer' }}>Herdada</Tag>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
    ];
    if (loading) return <Spin tip="Carregando..." />;
    if (error) return <Alert message={error} type="error" showIcon />;

    // Filtra entidades/contextos/itens pelo termo de busca. Quando o nome da
    // entidade casa, mostra tudo; senão, mantém apenas contextos/itens que casam.
    const buildEntity = (entity) => {
        const entityContexts = contextsByEntity[entity.entity] || [];
        const nonContextualItems = entity.items.filter((item) => !item.isContextual);
        const contextualItems = entity.items.filter((item) => item.isContextual);

        if (!term || matches(entity.name)) {
            return { entity, entityContexts, nonContextualItems, contextualItems };
        }

        const matchedContexts = entityContexts.filter((c) => matches(c.entity_name));
        const matchedNonCtx = nonContextualItems.filter((i) => matches(i.title) || matches(i.description));
        const hasContextualItemMatch = contextualItems.some((i) => matches(i.title) || matches(i.description));

        if (!matchedContexts.length && !matchedNonCtx.length && !hasContextualItemMatch) {
            return null;
        }

        return {
            entity,
            entityContexts: hasContextualItemMatch ? entityContexts : matchedContexts,
            nonContextualItems: matchedNonCtx,
            contextualItems,
        };
    };

    const countGranted = (list, context) =>
        list.reduce(
            (acc, item) => acc + (isPermissionChecked(context ? { ...item, context } : item) ? 1 : 0),
            0
        );

    const CountTag = ({ granted, total }) =>
        total > 0 ? (
            <Tag color={granted > 0 ? 'green' : 'default'} style={{ marginInlineStart: 8 }}>
                {granted}/{total}
            </Tag>
        ) : null;

    const visibleEntities = permissions.map(buildEntity).filter(Boolean);

    const items = visibleEntities.map(({ entity, entityContexts, nonContextualItems, contextualItems }) => {
        let ctxGranted = 0;
        let ctxTotal = 0;
        entityContexts.forEach((context) => {
            ctxGranted += countGranted(contextualItems, context);
            ctxTotal += contextualItems.length;
        });
        const totalGranted = countGranted(nonContextualItems) + ctxGranted;
        const totalPerms = nonContextualItems.length + ctxTotal;

        const pageSize = contextPageSize[entity.entity] || 10;
        const currentPage = contextPage[entity.entity] || 1;
        const pagedContexts = entityContexts.slice((currentPage - 1) * pageSize, currentPage * pageSize);

        const panels = pagedContexts.map((context) => {
            const granted = countGranted(contextualItems, context);
            return (
                <Panel
                    key={context.id}
                    header={
                        <span>
                            {`${entity.name}: ${context.entity_name}`}
                            <CountTag granted={granted} total={contextualItems.length} />
                        </span>
                    }
                >
                    <Table
                        size="small"
                        columns={getColumnsContext(contextualItems, context)}
                        dataSource={contextualItems.map((item) => ({ ...item, context }))}
                        pagination={false}
                        rowKey="id"
                    />
                </Panel>
            );
        });

        return (
            <Panel
                key={entity.entity}
                header={
                    <span style={{ fontWeight: 600 }}>
                        {entity.name}
                        <CountTag granted={totalGranted} total={totalPerms} />
                    </span>
                }
            >
                {nonContextualItems.length > 0 && (
                    <>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>
                            Configurações Gerais
                        </Text>
                        <Table
                            size="small"
                            columns={columnsNoContext}
                            dataSource={nonContextualItems}
                            pagination={false}
                            rowKey="id"
                        />
                    </>
                )}
                {entityContexts.length > 0 && (
                    <div style={{ marginTop: nonContextualItems.length > 0 ? 16 : 0 }}>
                        {/* Ao buscar, abre automaticamente os contextos que casam */}
                        <Collapse {...(term ? { activeKey: pagedContexts.map((c) => String(c.id)) } : {})}>
                            {panels}
                        </Collapse>
                        {entityContexts.length > pageSize && (
                            <div style={{ marginTop: 12, textAlign: 'right' }}>
                                <Pagination
                                    size="small"
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={entityContexts.length}
                                    showSizeChanger={{ showSearch: false }}
                                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                                    showTotal={(t) => `${t} formulários`}
                                    onChange={(p, ps) => {
                                        setContextPage((prev) => ({ ...prev, [entity.entity]: p }));
                                        setContextPageSize((prev) => ({ ...prev, [entity.entity]: ps }));
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </Panel>
        );
    });

    return (
        <div>
            <Input.Search
                allowClear
                placeholder="Buscar seção, formulário ou permissão..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ maxWidth: 420, marginBottom: 16 }}
            />
            {visibleEntities.length === 0 ? (
                <Empty description="Nenhuma permissão encontrada" />
            ) : (
                <Collapse
                    {...(term
                        ? { activeKey: visibleEntities.map((v) => v.entity.entity) }
                        : { activeKey: openEntities, onChange: setOpenEntities })}
                >
                    {items}
                </Collapse>
            )}
        </div>
    );
};
